import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Heart, ShoppingCart, ArrowLeft, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const { user } = useAuth();
  const { addToCart } = useCart();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products/${productId}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      toast.error('Product not found');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const showContactMessage = () => {
    toast(
      'To place an order, reach out to us!',
      {
        description: 'Contact us via our Contact page, Instagram, or email at navsartstudio@gmail.com',
        action: { label: 'Contact Us', onClick: () => navigate('/contact') },
        duration: 6000,
      }
    );
  };

  const nextImage = () => {
    if (product?.images && product.images.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % product.images.length);
    }
  };

  const prevImage = () => {
    if (product?.images && product.images.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + product.images.length) % product.images.length);
    }
  };

  const currentImage = product?.images?.[currentImageIndex] || product?.image_url;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAFAFA' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A1A1A]"></div>
      </div>
    );
  }

  if (!product) return null;

  const productJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: product.images && product.images.length ? product.images : [product.image_url],
    sku: product.id,
    brand: { '@type': 'Brand', name: 'Navs Art Studio' },
    category: product.category,
    offers: {
      '@type': 'Offer',
      url: `https://www.navsartstudio.com/product/${product.id}`,
      priceCurrency: 'USD',
      price: product.price,
      availability:
        product.availability === 'sold'
          ? 'https://schema.org/SoldOut'
          : 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
      seller: { '@type': 'Organization', name: 'Navs Art Studio' },
    },
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAFAFA' }}>
      <SEO
        title={product.name}
        description={`${product.description.slice(0, 155)}${product.description.length > 155 ? '…' : ''}`}
        path={`/product/${product.id}`}
        image={product.image_url}
        type="product"
        jsonLd={productJsonLd}
      />
      <div className="pt-32 pb-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            data-testid="product-back-btn"
            className="flex items-center gap-2 text-[#888888] hover:text-[#1A1A1A] transition-colors mb-8"
          >
            <ArrowLeft size={20} />
            <span className="text-sm uppercase tracking-widest">Back</span>
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
            {/* Image Gallery */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="sticky top-32">
                {/* Main Image with Carousel Controls */}
                <div className="relative group mb-4">
                  <img
                    src={currentImage}
                    alt={product.name}
                    data-testid="product-image"
                    className="w-full aspect-square object-cover rounded-lg cursor-pointer"
                    onClick={() => setIsZoomed(!isZoomed)}
                  />
                  
                  {/* Image Counter */}
                  {product.images && product.images.length > 1 && (
                    <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm">
                      {currentImageIndex + 1} / {product.images.length}
                    </div>
                  )}

                  {/* Zoom Icon */}
                  <button
                    onClick={() => setIsZoomed(!isZoomed)}
                    className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white"
                    data-testid="zoom-button"
                  >
                    <ZoomIn size={20} strokeWidth={1.5} />
                  </button>

                  {/* Navigation Arrows */}
                  {product.images && product.images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        data-testid="prev-image-btn"
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm p-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white"
                      >
                        <ChevronLeft size={24} strokeWidth={1.5} />
                      </button>
                      <button
                        onClick={nextImage}
                        data-testid="next-image-btn"
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm p-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white"
                      >
                        <ChevronRight size={24} strokeWidth={1.5} />
                      </button>
                    </>
                  )}
                </div>

                {/* Thumbnail Gallery */}
                {product.images && product.images.length > 1 && (
                  <div className="grid grid-cols-5 gap-2">
                    {product.images.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentImageIndex(index)}
                        data-testid={`thumbnail-${index}`}
                        className={`aspect-square rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                          currentImageIndex === index
                            ? 'border-[#1A1A1A] ring-2 ring-[#1A1A1A] ring-offset-2'
                            : 'border-transparent hover:border-[#E5E5E5]'
                        }`}
                      >
                        <img
                          src={image}
                          alt={`${product.name} - View ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                )}

                {product.badge && (
                  <div className="mt-4">
                    <span className="inline-block bg-[#1A1A1A] text-white px-4 py-2 text-xs uppercase tracking-widest">
                      {product.badge.replace('-', ' ')}
                    </span>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Details */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h1
                className="text-4xl lg:text-5xl mb-4"
                style={{ fontFamily: 'Playfair Display, serif' }}
                data-testid="product-name"
              >
                {product.name}
              </h1>
              
              <p className="text-3xl mb-8" data-testid="product-price">${product.price.toFixed(2)}</p>

              <div className="mb-8">
                <p className="text-[#4A4A4A] leading-relaxed mb-4">{product.description}</p>
                {product.story && (
                  <div className="mt-6 p-6 bg-[#F5F5F0] rounded-lg">
                    <h3 className="text-sm uppercase tracking-widest mb-3">The Story</h3>
                    <p
                      className="text-[#4A4A4A] leading-relaxed"
                      style={{ fontFamily: 'Cormorant Garamond, serif', fontStyle: 'italic' }}
                    >
                      {product.story}
                    </p>
                  </div>
                )}
              </div>

              {/* Specifications */}
              <div className="mb-8 pb-8 border-b border-black/5">
                <h3 className="text-sm uppercase tracking-widest mb-4">Details</h3>
                <div className="space-y-3">
                  {product.size && (
                    <div className="flex justify-between">
                      <span className="text-[#888888]">Size</span>
                      <span className="font-medium">{product.size}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-[#888888]">Availability</span>
                    <span className="font-medium capitalize">{product.availability}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#888888]">Category</span>
                    <span className="font-medium capitalize">{product.category.replace('-', ' ')}</span>
                  </div>
                </div>
              </div>

              {/* Features Section */}
              {product.features && product.features.length > 0 && (
                <div className="mb-8 pb-8 border-b border-black/5">
                  <h3 className="text-sm uppercase tracking-widest mb-4">Features</h3>
                  <ul className="space-y-2">
                    {product.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2 text-[#4A4A4A] text-sm">
                        <span className="text-[#1A1A1A] mt-1">•</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  {product.category === 'ocean' && (
                    <p className="text-sm text-[#888888] mt-4 italic">
                      For custom sizes or color variations, please contact us.
                    </p>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="space-y-4">
                <button
                  onClick={showContactMessage}
                  data-testid="product-add-to-cart-btn"
                  className="w-full bg-[#1A1A1A] text-white hover:bg-[#333333] rounded-full px-8 py-6 text-sm uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-3"
                >
                  <ShoppingCart size={20} strokeWidth={1.5} />
                  Add to Cart
                </button>
                <button
                  onClick={showContactMessage}
                  data-testid="product-add-to-wishlist-btn"
                  className="w-full bg-transparent border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white rounded-full px-8 py-6 text-sm uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-3"
                >
                  <Heart size={20} strokeWidth={1.5} />
                  Save to Wishlist
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Zoom Modal */}
      {isZoomed && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setIsZoomed(false)}
          data-testid="zoom-modal"
        >
          <button
            onClick={() => setIsZoomed(false)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
          >
            <span className="text-3xl">&times;</span>
          </button>
          <img
            src={currentImage}
            alt={product?.name}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {product?.images && product.images.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm p-4 rounded-full text-white hover:bg-white/30 transition-colors"
              >
                <ChevronLeft size={32} strokeWidth={1.5} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm p-4 rounded-full text-white hover:bg-white/30 transition-colors"
              >
                <ChevronRight size={32} strokeWidth={1.5} />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
